<template>
	<div class="DisciplineFilter">
		<SearchableRadioFilter
			:items="items"
			:options="options"
			:modelValue="selectedId"
			placeholder="Search by Discipline"
			description="Use the search field to find your discipline"
			groupName="discipline"
			:highlightSelectedText="true"
			@select="handleSelect"
		/>
	</div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { storeToRefs } from 'pinia';
import { useSearchStore } from '@stores/useSearchStore';
import SearchableRadioFilter from '@components/Filters/SearchableRadioFilter/SearchableRadioFilter.vue';
import { DisciplineFilterMapper } from '@app/routing/mappers/DisciplineFilterMapper';
import { FilterType } from '@studyportals/omnisearch-interfaces';
import { SelectListItemStateType } from '@app/enums/SelectListItemStateType';
import type ISelectItem from '@app/interfaces/dtos/ISelectItem';
import type { IMultiselectOption } from '@app/interfaces/dtos/IMultiselectOption';
import { subdisciplinesAll } from '@studyportals/static-domain-data/disciplines/subdisciplines';
import { IFacetKeyValuePair } from '@domain/interfaces/dtos/IFacetKeyValuePair';
import { useRollbar } from '@app/composables/useRollbar';

const searchStore = useSearchStore();
const { filtersSelection, pageData } = storeToRefs(searchStore);
const { logError } = useRollbar();

const mapper = DisciplineFilterMapper.getInstance();

const subdisciplineParentMap = new Map<string, string>();
subdisciplinesAll.forEach((sub: { id: string; disciplineId: string }) => {
	subdisciplineParentMap.set(sub.id.toString(), sub.disciplineId.toString());
});

const facetCounts = computed(() => {
	const facets = pageData.value?.filtersFacets ?? {} as Record<FilterType, IFacetKeyValuePair | undefined>;
	return facets[FilterType.DISCIPLINES];
});

const selectedId = computed<string | number | null>(() =>
	filtersSelection.value.find((f) => f.type === FilterType.DISCIPLINES)?.values[0]?.id ?? null
);

const subdisciplinesByDiscipline = computed(() => {
	const grouped = new Map<string, ISelectItem[]>();
	mapper.getSubdisciplines().forEach((sub) => {
		const parentId = subdisciplineParentMap.get(sub.id);
		if (!parentId) return;
		const list = grouped.get(parentId) ?? [];
		list.push({
			value: sub.id,
			text: sub.name ?? sub.id,
			icon: sub.icon,
			count: facetCounts.value?.[sub.id],
			state: selectedId.value === sub.id ? SelectListItemStateType.SELECTED : SelectListItemStateType.ENABLED
		});
		grouped.set(parentId, list);
	});
	return grouped;
});

const items = computed(() => {
	return mapper.getDisciplines().map((discipline) => {
		const subItems = subdisciplinesByDiscipline.value.get(discipline.id);
		return {
			item: {
			value: discipline.id,
			text: discipline.name ?? discipline.id,
			icon: discipline.icon,
			count: facetCounts.value?.[discipline.id],
			state: selectedId.value === discipline.id ? SelectListItemStateType.SELECTED : SelectListItemStateType.ENABLED
		} as ISelectItem,
		subItems
	};
});
});

const options = computed<IMultiselectOption[]>(() => {
	const opts: IMultiselectOption[] = [];
	mapper.getDisciplines().forEach((discipline) => {
		opts.push({
			value: discipline.id,
			label: discipline.name ?? discipline.id,
			count: facetCounts.value?.[discipline.id]
		});
	});
	mapper.getSubdisciplines().forEach((sub) => {
		opts.push({
			value: sub.id,
			label: sub.name ?? sub.id,
			count: facetCounts.value?.[sub.id]
		});
	});
	return opts;
});

const handleSelect = async (value: string | number, selected: boolean): Promise<void> => {
	const mappedValue = mapper.getExtendedFilterValueById(String(value));
	if (!mappedValue) return;

	let nextFilters = filtersSelection.value.filter((f) => f.type !== FilterType.DISCIPLINES);

	if (selected) {
		nextFilters = [...nextFilters, { type: FilterType.DISCIPLINES, values: [mappedValue] }];
	}

	searchStore.filtersSelection = nextFilters;
	try {
		await searchStore.setRouteAndLoadData();
	} catch (error) {
		logError('Failed to update discipline filter', error as Error);
	}
};
</script>

<style scoped lang="scss">
.DisciplineFilter {
	display: flex;
	flex-direction: column;
	width: 100%;
	gap: 1rem;
	@include AddBorderBeforeElement(1, 2);
}
</style>
